import {
  Column,
  ColumnGroup,
  LinkBehaviour,
  TableHeader,
  Typography,
} from '@cast/design-system';
import { Link } from '@mui/material';
import {
  dashesOrNum,
  formatPercentage,
  normalizeMemoryUnit,
} from '@cast/utils';

import { Link as RouterLink } from 'components/router';
import { formatLatencyWithUnit } from 'features/cluster/_components/reliability/_utils/formatLatency';
import { NamespacesLinkCell } from 'features/cost-report/components/NamespacesLinkCell';
import { WorkloadDetailsLinkCell } from 'features/cost-report/components/WorkloadDetailsLinkCell';
import { WorkloadReliabilityMetricsItem } from 'features/cost-report/types/workloadsReliability';
import { formatDailyMetricsCost } from 'features/cost-report/utils/metrics/formatDailyMetricsCost';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  NoData,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
  useSearchContext,
} from '@cast/shared-search-ui';

type ReliabilityWorkloadsTableProps = {
  cacheKey: string;
  disableWorkloadLink?: boolean;
  getWorkloadLink?: (workload: WorkloadReliabilityMetricsItem) => string;
};

export const ReliabilityWorkloadsTable = ({
  cacheKey,
  disableWorkloadLink,
  getWorkloadLink,
}: ReliabilityWorkloadsTableProps) => {
  const searchCtx = useSearchContext<WorkloadReliabilityMetricsItem>();

  return (
    <SearchableTable
      cacheKey={cacheKey}
      testId="workloads-reliability-table"
      maxHeight="80vh"
      components={{
        noData: <NoData entity="workload" />,
        noResults: <NoResults entity="workload" />,
        failed: <FailedToLoad />,
      }}
      outerHeader={
        <TableHeader
          recordsCount={
            <SearchableTableRecordsCount
              title="workload"
              outOf={searchCtx?.metadata}
            />
          }
        />
      }
      sizing="l"
    >
      <Column<WorkloadReliabilityMetricsItem>
        id="workloadName"
        header="Workload"
        minWidth={220}
        resizable
        renderCell={(workload) => {
          const { workloadName, namespace, workloadType } = workload;

          if (disableWorkloadLink) {
            return (
              <Typography variant="inherit" fontWeight={500} noWrap>
                {workloadName}
              </Typography>
            );
          }

          if (getWorkloadLink) {
            return (
              <Link
                to={getWorkloadLink(workload)}
                component={RouterLink}
                underline="none"
              >
                <LinkBehaviour variant="inherit" fontWeight={500} noWrap>
                  {workloadName}
                </LinkBehaviour>
              </Link>
            );
          }

          return (
            <WorkloadDetailsLinkCell
              workload={workloadName}
              namespace={namespace}
              workloadType={workloadType}
            />
          );
        }}
        sortable
      />
      <Column id="workloadType" header="Type" minWidth={110} sortable />
      <Column<WorkloadReliabilityMetricsItem>
        id="namespace"
        header="Namespace"
        minWidth={130}
        renderCell={({ namespace }) => (
          <NamespacesLinkCell namespace={namespace} />
        )}
        sortable
      />
      <Column<WorkloadReliabilityMetricsItem>
        id="podCount"
        header="Pods"
        minWidth={90}
        infoTooltip="Average hourly pods count"
        renderCell={({ cost }) =>
          dashesOrNum({ value: cost?.podCount, roundBy: 0 })
        }
      />
      <ColumnGroup id="reliability-signals" title="RELIABILITY">
        <Column<WorkloadReliabilityMetricsItem>
          id="requestRate"
          header="Request Rate"
          minWidth={130}
          infoTooltip="Requests per second."
          renderCell={({ reliability }) =>
            `${dashesOrNum({
              value: reliability?.requestRate,
              roundBy: 2,
            })} req/s`
          }
          sortable
        />
        <Column<WorkloadReliabilityMetricsItem>
          id="errorRate"
          header="Error Rate"
          minWidth={120}
          infoTooltip="Errors per second."
          renderCell={({ reliability }) =>
            `${dashesOrNum({
              value: reliability?.errorRate,
              roundBy: 2,
            })} req/s`
          }
          sortable
        />
        <Column<WorkloadReliabilityMetricsItem>
          id="errorRatePercent"
          header="Error Rate"
          minWidth={120}
          infoTooltip="Error rate as a percentage of failed requests over total requests."
          renderCell={({ reliability }) =>
            formatPercentage(reliability?.errorRatePercent)
          }
          sortable
        />
        <Column<WorkloadReliabilityMetricsItem>
          id="latencyP95"
          header="P95 Latency"
          minWidth={140}
          infoTooltip="95th percentile latency."
          renderCell={({ reliability }) =>
            formatLatencyWithUnit(
              reliability?.latencyP95
                ? reliability.latencyP95 * 1000
                : undefined,
              2
            )
          }
          sortable
        />
      </ColumnGroup>
      <ColumnGroup id="avg-req" title="AVG. REQ. PER HOUR">
        <Column<WorkloadReliabilityMetricsItem>
          id="requestedCpuCores"
          header="CPU"
          minWidth={110}
          infoTooltip="Average hourly CPU requests per workload"
          renderCell={({ cost }) =>
            `${dashesOrNum({ value: cost?.requestedCpuCores, roundBy: 2 })} CPU`
          }
        />
        <Column<WorkloadReliabilityMetricsItem>
          id="requestedRamGib"
          header="Memory"
          minWidth={110}
          infoTooltip="Average hourly memory requests per workload"
          renderCell={({ cost }) =>
            dashesOrNum({
              value: cost?.requestedRamGib,
              mapNumber: (gib) => {
                const value = normalizeMemoryUnit({ amount: gib, unit: 'GiB' });
                return `${value.amount} ${value.unit}`;
              },
              mapDashes: (dashes) => `${dashes} GiB`,
              roundBy: 2,
            })
          }
        />
      </ColumnGroup>
      <Column<WorkloadReliabilityMetricsItem>
        id="totalCost"
        header="Total Cost"
        minWidth={120}
        infoTooltip="Sum of all pod costs in the workload"
        renderCell={({ cost }) => (
          <Typography variant="inherit" fontWeight="bold" noWrap>
            {dashesOrNum({
              value: cost?.totalCost,
              mapNumber: formatDailyMetricsCost,
            })}
          </Typography>
        )}
      />
    </SearchableTable>
  );
};
